import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import Autocomplete from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import useTheme from "@mui/material/styles/useTheme";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAddInState } from "../AddInStateProvider";
import { useGroups } from "@/hooks/groups/useGroups";
import { useRememberedGroups } from "@/hooks/userSettings/useRememberedGroups";

export function GroupSelect() {
  const { t } = useTranslation();
  const { data: allGroups } = useGroups();
  const { groups, updateGroups } = useAddInState();
  const theme = useTheme();

  // Wrapped, because the hook only ever calls this once and holding it stable keeps the restore
  // from being reconsidered on every render.
  const onRestore = useCallback(updateGroups, [updateGroups]);
  const { remember, setRemember, recordSelection } = useRememberedGroups("headerGroupFilter", {
    available: allGroups,
    selected: groups,
    onRestore,
  });

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      sx={{ width: "100%", minWidth: "300px", maxWidth: "440px" }}
    >
      <Autocomplete
        id="group-search"
        size="small"
        multiple
        value={groups ?? []}
        onChange={(_, value) => {
          updateGroups(value);
          recordSelection(value);
        }}
        sx={{
          flex: 1,
          backgroundColor: theme.palette.primary.main,
          "& .MuiAutocomplete-popupIndicator": {
            color: "white",
          },
          "& .MuiInputBase-input": { color: "white" },
          "& .MuiChip-label": {
            color: "white",
          },
          "& .MuiChip-deleteIcon": {
            color: "white",
          },
          "& .MuiAutocomplete-clearIndicator": {
            color: "white",
          },
          "& .MuiChip-root, & .MuiAutocomplete-tag": {
            color: "white",
          },
          "& .MuiAutocomplete-tag.MuiTypography-root": {
            color: "white",
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": { borderColor: theme.palette.primary.main },
            "&:hover fieldset": { borderColor: theme.palette.primary.main },
            "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
          },
        }}
        options={allGroups ?? []}
        limitTags={1}
        getOptionKey={option => option.id}
        getOptionLabel={option => option.name ?? ""}
        filterSelectedOptions
        disableCloseOnSelect
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={params => (
          <TextField {...params} placeholder={t("faultOverview.groupSearchPlaceholder")} />
        )}
      />
      <Tooltip
        title={
          remember
            ? t("groupFilter.forget", "Gruppenauswahl nicht mehr merken")
            : t("groupFilter.remember", "Gruppenauswahl für mich merken")
        }
      >
        <IconButton
          size="small"
          onClick={() => setRemember(!remember)}
          aria-pressed={remember}
          sx={{ color: "white" }}
        >
          {remember ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
